// 🔬 B242 (AI_111) — Aba PESQUISA CLÍNICA (Cohort Explorer, MVP1)
// ─────────────────────────────────────────────────────────────────────────────
// REGRA DE OURO: aqui é só CÓDIGO contando/calculando (sem IA). A tela:
//   1) consome os contextos já carregados (pacientes/exames/sorologias),
//   2) monta o RegistroPesquisa[] PSEUDONIMIZADO (carregarRegistros),
//   3) aplica filtros de um catálogo curado → coorte (motorCoorte),
//   4) exibe n + Table 1 descritiva (estratificável) + missingness,
//   5) MASCARA células com n<5 (anti-reidentificação).
// Gate de papel (médico/admin) é feito no App.tsx (ehMedico). Nenhuma leitura
// nova no banco — tudo vem da memória dos contextos.
import React, { useMemo, useState, useEffect } from "react";
import { BarChart3, Plus, X, Filter, AlertTriangle, HelpCircle, Sparkles, FileText, Download, Search, Users } from "lucide-react";
import { theme } from "../../../shared/styles/theme";
import TextoAnaliseMarkdown from "../../../shared/components/TextoAnaliseMarkdown";
import { usePacientes } from "../../pacientes/context/PacientesContext";
import { useExames } from "../../exames/context/ExamesContext";
import { useSorologias } from "../../sorologias/context/SorologiasContext";
import { useMarcadores } from "../../marcadores/context/MarcadoresContext";
import { montarRegistros } from "../services/carregarRegistros";
import { aplicarFiltros, montarTabela1 } from "../services/motorCoorte";
import { CAMPOS_PESQUISA, CAMPO_POR_CHAVE } from "../data/catalogoCampos";
import { N_MINIMO_CELULA } from "../services/estatisticasDescritivas";
// B242 MVP3a — operadoresDe/precisaValor vivem no validador (fonte única: o
// mesmo conjunto de operadores que a IA pode propor é o que a UI edita).
import {
  operadoresDe,
  precisaValor,
  catalogoParaIA,
  validarPlanoIA,
  PlanoIAValidado,
} from "../services/planoIA";
import { callAnalyze } from "../../../lib/analyzeClient";
import { tabela1ParaCSV, MetaExport } from "../services/exportar";
import GraficoCoorte from "./GraficoCoorte";
import TabelaPacientesPesquisa, { LinhaPaciente } from "./TabelaPacientesPesquisa";
import BuscaAvancada from "./BuscaAvancada";
import MapaCompletude from "./MapaCompletude";
import {
  Filtro,
  Operador,
  CelulaTabela1,
  LinhaTabela1,
} from "../types";

// ── Rótulos humanos dos operadores ────────────────────────────────────────────
const ROTULO_OPERADOR: Record<Operador, string> = {
  igual: "é igual a",
  diferente: "é diferente de",
  maior: "maior que",
  maiorIgual: "maior ou igual a",
  menor: "menor que",
  menorIgual: "menor ou igual a",
  entre: "entre",
  presente: "tem valor",
  ausente: "está em branco",
  contem: "contém",
};

// ── Estilos base ──────────────────────────────────────────────────────────────
const card: React.CSSProperties = {
  background: "white",
  border: `1px solid ${theme.gray[200]}`,
  borderRadius: theme.borderRadius.lg,
  padding: "16px",
  marginBottom: "16px",
  fontFamily: theme.fonts.sans,
};
const inputStyle: React.CSSProperties = {
  padding: "8px 10px",
  border: `1px solid ${theme.gray[300]}`,
  borderRadius: theme.borderRadius.md,
  fontSize: "13px",
  fontFamily: theme.fonts.sans,
  background: "white",
  color: theme.gray[800],
};
const labelMini: React.CSSProperties = {
  fontSize: "11px",
  fontWeight: 600,
  color: theme.gray[500],
  marginBottom: "4px",
  display: "block",
};

export default function AbaPesquisaClinica() {
  const { pacientes, selecionarPaciente } = usePacientes();
  const { exames } = useExames();
  const { sorologias } = useSorologias();
  const { marcadores } = useMarcadores();

  // 1) Registros pseudonimizados (recalcula só quando os dados mudam).
  const registros = useMemo(
    () =>
      montarRegistros(
        pacientes as any,
        exames as any,
        sorologias as any,
        undefined,
        marcadores as any
      ),
    [pacientes, exames, sorologias, marcadores]
  );

  const [mostrarInstrucoes, setMostrarInstrucoes] = useState(false);
  // B242 (AI_112) — visão: "coorte" (pesquisa/estatística) × "busca" (Busca Avançada).
  // B251 (AI_115) — + "completude" (mapa de qualidade: o que falta preencher).
  const [view, setView] = useState<"coorte" | "completude" | "busca">("coorte");
  // B242 MVP2 — variável escolhida para o gráfico (chave do catálogo; "" = nenhum).
  const [campoGrafico, setCampoGrafico] = useState("");
  // B242 MVP2 — finalidade declarada do export (gate de governança leve).
  const [finalidade, setFinalidade] = useState("Gestão/qualidade interna");
  // B242 MVP4 — termo da busca textual (vira um filtro "contém" em _textoBusca).
  const [termoBusca, setTermoBusca] = useState("");

  function aplicarBuscaTexto() {
    const t = termoBusca.trim();
    setFiltros((fs) => {
      const semBusca = fs.filter((f) => f.campo !== "_textoBusca");
      return t ? [...semBusca, { campo: "_textoBusca", operador: "contem", valor: t }] : semBusca;
    });
  }

  // 2) Estado do construtor de coorte.
  const [filtros, setFiltros] = useState<Filtro[]>([]);
  const [conector, setConector] = useState<"E" | "OU">("E");
  const [estratificarPor, setEstratificarPor] = useState<string>("");

  // 2b) B242 MVP3a — "IA monta a coorte": pergunta livre → plano proposto.
  // A IA só PROPÕE; o código (validarPlanoIA) sanea e o médico confirma antes
  // de aplicar. Nenhum dado de paciente é enviado — só o catálogo de campos.
  const [perguntaIA, setPerguntaIA] = useState("");
  const [carregandoIA, setCarregandoIA] = useState(false);
  const [proposta, setProposta] = useState<PlanoIAValidado | null>(null);
  const [erroIA, setErroIA] = useState("");

  async function perguntarIA() {
    const q = perguntaIA.trim();
    if (!q || carregandoIA) return;
    setCarregandoIA(true);
    setErroIA("");
    setProposta(null);
    try {
      const resp = await callAnalyze<{ plano?: unknown; erro?: string }>({
        etapa: "pesquisa-plano",
        pergunta: q,
        catalogo: catalogoParaIA(CAMPOS_PESQUISA),
      });
      if (resp.erro || !resp.plano) {
        setErroIA("Não consegui montar a coorte agora. Tente reformular a pergunta.");
      } else {
        setProposta(validarPlanoIA(resp.plano as any, CAMPOS_PESQUISA));
      }
    } catch (e) {
      setErroIA("Erro ao falar com a IA. Verifique a conexão e tente novamente.");
    }
    setCarregandoIA(false);
  }

  function aplicarProposta() {
    if (!proposta) return;
    setFiltros(proposta.filtros);
    setConector(proposta.conector);
    setEstratificarPor(proposta.estratificarPor);
    setProposta(null);
    setPerguntaIA("");
  }

  // Move uma aproximação sugerida para os filtros da proposta (e some da lista).
  function adicionarAproximacao(filtro: Filtro) {
    setProposta((p) =>
      p
        ? {
            ...p,
            filtros: [...p.filtros, filtro],
            aproximacoes: p.aproximacoes.filter((a) => a.filtro !== filtro),
          }
        : p
    );
  }

  // 2c) B242 MVP3b — "IA interpreta a Table 1": a IA narra os números que o CÓDIGO
  // já calculou. Envia SÓ agregados (Table 1 mascarada), nunca dado de paciente.
  const [interpretacao, setInterpretacao] = useState("");
  const [carregandoInterpretacao, setCarregandoInterpretacao] = useState(false);
  const [erroInterpretacao, setErroInterpretacao] = useState("");

  // A interpretação fica obsoleta quando a coorte muda → limpa.
  useEffect(() => {
    setInterpretacao("");
    setErroInterpretacao("");
  }, [filtros, conector, estratificarPor]);

  async function interpretarComIA() {
    if (carregandoInterpretacao || coorte.length === 0) return;
    setCarregandoInterpretacao(true);
    setErroInterpretacao("");
    setInterpretacao("");
    try {
      const resp = await callAnalyze<{ interpretacao?: string; erro?: string }>({
        etapa: "pesquisa-interpreta",
        contexto: serializarContextoTabela(),
      });
      if (resp.erro || !resp.interpretacao) {
        setErroInterpretacao("Não consegui interpretar agora. Tente novamente.");
      } else {
        setInterpretacao(resp.interpretacao);
      }
    } catch (e) {
      setErroInterpretacao("Erro ao falar com a IA. Verifique a conexão e tente novamente.");
    }
    setCarregandoInterpretacao(false);
  }

  // B242 MVP2 — Exporta a Table 1 (agregada, mascarada) em CSV. Não exporta dado
  // por paciente (isso exige a camada de governança/CEP). Download client-side.
  function exportarCSV() {
    if (coorte.length === 0) return;
    const meta: MetaExport = {
      geradoEm: new Date().toLocaleString("pt-BR"),
      finalidade,
      n: coorte.length,
      filtros: filtros.length > 0 ? filtros.map(rotuloFiltro).join("; ") : "nenhum (base inteira)",
      conector: conector === "OU" ? "OU (qualquer)" : "E (todos)",
      estratificacao: estratificarPor
        ? CAMPO_POR_CHAVE[estratificarPor]?.label ?? estratificarPor
        : "nenhuma",
    };
    const csv = tabela1ParaCSV(meta, colunas, tabela);
    // BOM (﻿) para o Excel reconhecer UTF-8 (acentos).
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `pesquisa-table1-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // Serializa a coorte + Table 1 (com a MESMA máscara n<5 da tela) num texto
  // agregado p/ a IA. Nenhum identificador entra — só números já calculados.
  function serializarContextoTabela(): string {
    const linhasFiltro =
      filtros.length > 0 ? filtros.map(rotuloFiltro).join("; ") : "nenhum (coorte = base inteira)";
    const estrato = estratificarPor ? CAMPO_POR_CHAVE[estratificarPor]?.label : "";
    const partes: string[] = [
      "## Coorte",
      `- n = ${coorte.length} paciente(s)`,
      `- Filtros (${conector === "OU" ? "qualquer um" : "todos juntos"}): ${linhasFiltro}`,
      `- Estratificação: ${estrato || "nenhuma"}`,
      "",
      "## Tabela descritiva (Table 1)",
      `Colunas: ${colunas.join(" | ")}`,
      'Numéricos: "Mediana [Q1–Q3]", "Média (DP)", "Mín–Máx". Categóricos: "categoria: n (%)". "< 5" = mascarado por privacidade (poucos casos).',
    ];
    for (const linha of tabela) {
      const porColuna: Record<string, CelulaTabela1[]> = {};
      for (const cc of linha.celulasPorColuna) porColuna[cc.coluna] = cc.celulas;
      const rotulos = (porColuna["Total"] || []).map((c) => c.rotulo);
      partes.push(`\n### ${linha.label} — faltantes ${linha.faltantes} (${linha.faltantesPct}%)`);
      for (const col of colunas) {
        const cels = porColuna[col] || [];
        const txt = rotulos
          .map((rot) => {
            const cel = cels.find((c) => c.rotulo === rot);
            return cel ? `${rot}: ${textoCelula(cel)}` : `${rot}: —`;
          })
          .join(" | ");
        partes.push(`- ${col}: ${txt}`);
      }
    }
    return partes.join("\n");
  }

  // Rascunho do filtro em construção.
  const [campoSel, setCampoSel] = useState<string>(CAMPOS_PESQUISA[0].chave);
  const campoAtual = CAMPO_POR_CHAVE[campoSel];
  const opsDisponiveis = operadoresDe(campoAtual.tipo);
  const [operadorSel, setOperadorSel] = useState<Operador>(opsDisponiveis[0]);
  const [valorSel, setValorSel] = useState<string>("");
  const [valor2Sel, setValor2Sel] = useState<string>("");

  function aoTrocarCampo(novaChave: string) {
    setCampoSel(novaChave);
    const ops = operadoresDe(CAMPO_POR_CHAVE[novaChave].tipo);
    setOperadorSel(ops[0]);
    setValorSel("");
    setValor2Sel("");
  }

  function adicionarFiltro() {
    if (precisaValor(operadorSel) && valorSel === "") return;
    const novo: Filtro = {
      campo: campoSel,
      operador: operadorSel,
      valor: precisaValor(operadorSel) ? valorSel : undefined,
      valor2: operadorSel === "entre" ? valor2Sel : undefined,
    };
    setFiltros((f) => [...f, novo]);
    setValorSel("");
    setValor2Sel("");
  }

  function removerFiltro(i: number) {
    setFiltros((f) => f.filter((_, idx) => idx !== i));
  }

  // 3) Coorte (código conta) + Table 1.
  const coorte = useMemo(
    () => aplicarFiltros(registros, { conector, filtros }),
    [registros, conector, filtros]
  );
  const tabela = useMemo(
    () =>
      montarTabela1(coorte, CAMPOS_PESQUISA, {
        estratificarPor: estratificarPor || undefined,
      }),
    [coorte, estratificarPor]
  );

  const camposEstratificaveis = CAMPOS_PESQUISA.filter(
    (c) => c.tipo === "categorico" || c.tipo === "booleano"
  );

  // B242 — LISTA CLÍNICA (identificada) dos pacientes da coorte. Visão de gestão
  // (médico-only): mapeia cada registro de volta ao paciente para nome/abrir.
  const [mostrarPacientes, setMostrarPacientes] = useState(false);
  const pacientesPorId = useMemo(() => {
    const m = new Map<string, any>();
    for (const p of pacientes as any[]) if (p?.id) m.set(p.id, p);
    return m;
  }, [pacientes]);

  const linhasCoorte: LinhaPaciente[] = useMemo(() => {
    return coorte
      .map((r) => {
        const id = r._pacienteId as string;
        const p = pacientesPorId.get(id);
        if (!p) return null;
        return {
          id,
          nome: p.nome || "(sem nome)",
          grupo: (r.grupo as string) || null,
          status: (r.statusTransplante as string) || null,
          rgct: p.inscricaoTx?.numeroRGCT || p.numeroRGCT || p.rgct || "",
        } as LinhaPaciente;
      })
      .filter((x): x is LinhaPaciente => x !== null);
  }, [coorte, pacientesPorId]);

  function abrirPaciente(id: string) {
    const p = pacientesPorId.get(id);
    if (p) selecionarPaciente(p);
  }

  // ── Render do input de valor conforme tipo/operador ──────────────────────────
  function renderValor() {
    if (!precisaValor(operadorSel)) return null;
    const tipo = campoAtual.tipo;

    if (operadorSel === "entre") {
      return (
        <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
          <input
            type="number"
            value={valorSel}
            onChange={(e) => setValorSel(e.target.value)}
            placeholder="mín"
            style={{ ...inputStyle, width: "80px" }}
          />
          <span style={{ color: theme.gray[500] }}>e</span>
          <input
            type="number"
            value={valor2Sel}
            onChange={(e) => setValor2Sel(e.target.value)}
            placeholder="máx"
            style={{ ...inputStyle, width: "80px" }}
          />
        </div>
      );
    }

    if (tipo === "numerico") {
      return (
        <input
          type="number"
          value={valorSel}
          onChange={(e) => setValorSel(e.target.value)}
          placeholder="valor"
          style={{ ...inputStyle, width: "120px" }}
        />
      );
    }

    if (tipo === "booleano") {
      return (
        <select value={valorSel} onChange={(e) => setValorSel(e.target.value)} style={inputStyle}>
          <option value="">selecione…</option>
          <option value="true">Sim</option>
          <option value="false">Não</option>
        </select>
      );
    }

    if (tipo === "categorico" && campoAtual.opcoes && campoAtual.opcoes.length > 0) {
      return (
        <select value={valorSel} onChange={(e) => setValorSel(e.target.value)} style={inputStyle}>
          <option value="">selecione…</option>
          {campoAtual.opcoes.map((o) => (
            <option key={o.valor} value={o.valor}>
              {o.label}
            </option>
          ))}
        </select>
      );
    }

    // categórico sem opções curadas (ex.: sorologias) ou texto
    return (
      <input
        type="text"
        value={valorSel}
        onChange={(e) => setValorSel(e.target.value)}
        placeholder={tipo === "texto" ? "texto a procurar" : "valor"}
        style={{ ...inputStyle, width: "180px" }}
      />
    );
  }

  function rotuloFiltro(f: Filtro): string {
    // B242 MVP4 — filtro de busca textual (campo sintético concatenado).
    if (f.campo === "_textoBusca") return `Texto contém “${f.valor}”`;
    const campo = CAMPO_POR_CHAVE[f.campo];
    const nome = campo ? campo.label : f.campo;
    if (!precisaValor(f.operador)) return `${nome} ${ROTULO_OPERADOR[f.operador]}`;
    if (f.operador === "entre") return `${nome} entre ${f.valor} e ${f.valor2}`;
    let v = String(f.valor ?? "");
    // Mostra o rótulo amigável de opções/booleano.
    if (campo?.opcoes) v = campo.opcoes.find((o) => o.valor === v)?.label ?? v;
    if (campo?.tipo === "booleano") v = v === "true" ? "Sim" : v === "false" ? "Não" : v;
    return `${nome} ${ROTULO_OPERADOR[f.operador]} ${v}`;
  }

  const colunas = tabela[0]?.celulasPorColuna.map((c) => c.coluna) ?? ["Total"];
  const coorteVazia = coorte.length === 0;
  const coortePequena = coorte.length > 0 && coorte.length < N_MINIMO_CELULA;

  // B251 — eco read-only dos filtros ativos (texto), para o mapa de completude.
  const resumoFiltros =
    filtros.length > 0
      ? filtros.map(rotuloFiltro).join(conector === "OU" ? " OU " : " E ")
      : "todos os pacientes (sem filtro)";

  return (
    <div style={{ fontFamily: theme.fonts.sans }}>
      {/* Cabeçalho */}
      <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
        <BarChart3 size={22} color={theme.primary[600]} />
        <h2 style={{ fontSize: "22px", fontWeight: 700, color: theme.gray[800], margin: 0 }}>
          Pesquisa Clínica
        </h2>
        <button
          onClick={() => setMostrarInstrucoes((v) => !v)}
          style={{
            marginLeft: "auto",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            padding: "6px 12px",
            background: mostrarInstrucoes ? theme.primary[50] : "white",
            color: theme.primary[700],
            border: `1px solid ${theme.primary[300]}`,
            borderRadius: theme.borderRadius.md,
            fontSize: "13px",
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: theme.fonts.sans,
          }}
        >
          <HelpCircle size={15} /> Instruções
        </button>
      </div>
      <p style={{ color: theme.gray[500], fontSize: "13px", marginTop: 0, marginBottom: "16px" }}>
        Explorador de coorte — os dados são <strong>pseudonimizados</strong> (sem nome/CPF) e
        contados pelo código. Células com menos de {N_MINIMO_CELULA} pacientes são mascaradas.
        Base: {registros.length} paciente(s).
      </p>

      {mostrarInstrucoes && <PainelInstrucoes />}

      {/* B242 — alternador de visão: Pesquisa (coorte) × Busca Avançada (lista) */}
      <div
        style={{
          display: "flex",
          gap: "4px",
          margin: "0 0 16px",
          borderBottom: `1px solid ${theme.gray[200]}`,
        }}
      >
        {([
          ["coorte", "Pesquisa (coorte)"],
          ["completude", "Completude"],
          ["busca", "Busca Avançada"],
        ] as const).map(([v, rotulo]) => (
          <button
            key={v}
            onClick={() => setView(v)}
            style={{
              padding: "8px 16px",
              background: "none",
              border: "none",
              borderBottom: view === v ? `2px solid ${theme.primary[600]}` : "2px solid transparent",
              color: view === v ? theme.primary[700] : theme.gray[500],
              fontWeight: 600,
              fontSize: "14px",
              cursor: "pointer",
              fontFamily: theme.fonts.sans,
              marginBottom: "-1px",
            }}
          >
            {rotulo}
          </button>
        ))}
      </div>

      {view === "busca" ? (
        <BuscaAvancada
          registros={registros}
          pacientesPorId={pacientesPorId}
          onAbrir={abrirPaciente}
        />
      ) : view === "completude" ? (
        <MapaCompletude
          registros={coorte}
          pacientesPorId={pacientesPorId}
          onAbrir={abrirPaciente}
          resumoFiltros={resumoFiltros}
          onIrParaFiltros={() => setView("coorte")}
        />
      ) : (
       <>

      {/* B242 MVP3a — Perguntar à IA (a IA propõe o filtro; o código conta) */}
      <div style={{ ...card, background: theme.primary[50], border: `1px solid ${theme.primary[200]}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
          <Sparkles size={16} color={theme.primary[600]} />
          <strong style={{ fontSize: "14px", color: theme.primary[800] }}>
            Perguntar à IA (monta a coorte)
          </strong>
        </div>
        <p style={{ fontSize: "12px", color: theme.gray[600], margin: "0 0 10px" }}>
          Descreva sua pergunta em português — ex.: <em>“homens com cirrose por álcool, pré-transplante
          de fígado, MELD acima de 15”</em>. A IA <strong>sugere</strong> os filtros; quem conta os
          pacientes é o sistema. Revise e aplique. Nenhum dado de paciente é enviado à IA — só os nomes
          dos campos.
        </p>
        <div style={{ display: "flex", gap: "8px", alignItems: "flex-end" }}>
          <textarea
            value={perguntaIA}
            onChange={(e) => setPerguntaIA(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) perguntarIA();
            }}
            placeholder="Digite sua pergunta de pesquisa…"
            disabled={carregandoIA}
            rows={2}
            style={{ ...inputStyle, flex: 1, resize: "vertical", minHeight: "44px" }}
          />
          <button
            onClick={perguntarIA}
            disabled={carregandoIA || perguntaIA.trim() === ""}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              padding: "10px 16px",
              background:
                carregandoIA || perguntaIA.trim() === "" ? theme.gray[300] : theme.gradients.primary,
              color: "white",
              border: "none",
              borderRadius: theme.borderRadius.md,
              fontSize: "13px",
              fontWeight: 600,
              cursor: carregandoIA || perguntaIA.trim() === "" ? "not-allowed" : "pointer",
              whiteSpace: "nowrap",
            }}
          >
            <Sparkles size={14} /> {carregandoIA ? "Montando…" : "Propor coorte"}
          </button>
        </div>

        {erroIA && (
          <div style={{ marginTop: "10px", fontSize: "12px", color: theme.status.error.text }}>
            {erroIA}
          </div>
        )}

        {proposta && (
          <PropostaIA
            proposta={proposta}
            rotuloFiltro={rotuloFiltro}
            onAplicar={aplicarProposta}
            onAdicionar={adicionarAproximacao}
            onDescartar={() => setProposta(null)}
          />
        )}
      </div>

      {/* B242 MVP4 — Busca por palavra (versão segura: textos da pesquisa) */}
      <div style={card}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
          <Search size={16} color={theme.gray[600]} />
          <strong style={{ fontSize: "14px", color: theme.gray[700] }}>Busca por palavra</strong>
        </div>
        <p style={{ fontSize: "12px", color: theme.gray[500], margin: "0 0 10px" }}>
          Procura o termo no <strong>perfil clínico</strong> do paciente — etiologia, comorbidades,
          situação especial, HCC/BCLC/Milão e condições (ascite, varizes, encefalopatia, PBE, síndromes
          hepatorrenal/hepatopulmonar…). Cria um filtro <strong>“contém”</strong>; ignora acento e
          maiúsculas. Ex.: <em>hcc</em>, <em>varizes</em>, <em>colangite</em>, <em>hepatopulmonar</em>.
        </p>
        <div style={{ display: "flex", gap: "8px" }}>
          <input
            type="text"
            value={termoBusca}
            onChange={(e) => setTermoBusca(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") aplicarBuscaTexto();
            }}
            placeholder="ex.: colangite, álcool, hepatopulmonar…"
            style={{ ...inputStyle, flex: 1 }}
          />
          <button
            onClick={aplicarBuscaTexto}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              padding: "8px 16px",
              background: theme.gradients.primary,
              color: "white",
              border: "none",
              borderRadius: theme.borderRadius.md,
              fontSize: "13px",
              fontWeight: 600,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            <Search size={14} /> Buscar
          </button>
        </div>
      </div>

      {/* Construtor de filtros */}
      <div style={card}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
          <Filter size={16} color={theme.gray[600]} />
          <strong style={{ fontSize: "14px", color: theme.gray[700] }}>Construir coorte</strong>
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "flex-end" }}>
          <div>
            <label style={labelMini}>Campo</label>
            <select value={campoSel} onChange={(e) => aoTrocarCampo(e.target.value)} style={inputStyle}>
              {Array.from(new Set(CAMPOS_PESQUISA.map((c) => c.grupo || "Outros"))).map((grupo) => (
                <optgroup key={grupo} label={grupo}>
                  {CAMPOS_PESQUISA.filter((c) => (c.grupo || "Outros") === grupo).map((c) => (
                    <option key={c.chave} value={c.chave}>
                      {c.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          <div>
            <label style={labelMini}>Condição</label>
            <select
              value={operadorSel}
              onChange={(e) => setOperadorSel(e.target.value as Operador)}
              style={inputStyle}
            >
              {opsDisponiveis.map((op) => (
                <option key={op} value={op}>
                  {ROTULO_OPERADOR[op]}
                </option>
              ))}
            </select>
          </div>

          {precisaValor(operadorSel) && (
            <div>
              <label style={labelMini}>Valor</label>
              {renderValor()}
            </div>
          )}

          <button
            onClick={adicionarFiltro}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              padding: "8px 14px",
              background: theme.gradients.primary,
              color: "white",
              border: "none",
              borderRadius: theme.borderRadius.md,
              fontSize: "13px",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            <Plus size={14} /> Adicionar filtro
          </button>
        </div>

        {/* Filtros ativos */}
        {filtros.length > 0 && (
          <div style={{ marginTop: "14px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
              <span style={{ fontSize: "12px", color: theme.gray[500] }}>Combinar com:</span>
              {(["E", "OU"] as const).map((c) => (
                <button
                  key={c}
                  onClick={() => setConector(c)}
                  style={{
                    padding: "4px 12px",
                    borderRadius: theme.borderRadius.full,
                    border: `1px solid ${conector === c ? theme.primary[500] : theme.gray[300]}`,
                    background: conector === c ? theme.primary[50] : "white",
                    color: conector === c ? theme.primary[700] : theme.gray[600],
                    fontSize: "12px",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  {c === "E" ? "E (todos)" : "OU (qualquer)"}
                </button>
              ))}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {filtros.map((f, i) => (
                <span
                  key={i}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "4px 10px",
                    background: theme.gray[100],
                    border: `1px solid ${theme.gray[200]}`,
                    borderRadius: theme.borderRadius.full,
                    fontSize: "12px",
                    color: theme.gray[700],
                  }}
                >
                  {rotuloFiltro(f)}
                  <X
                    size={14}
                    style={{ cursor: "pointer", color: theme.gray[400] }}
                    onClick={() => removerFiltro(i)}
                  />
                </span>
              ))}
              <button
                onClick={() => setFiltros([])}
                style={{
                  fontSize: "12px",
                  color: theme.status.error.border,
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
              >
                limpar tudo
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Resultado: n da coorte */}
      <div style={{ ...card, display: "flex", alignItems: "center", gap: "16px" }}>
        <div>
          <div style={{ fontSize: "12px", color: theme.gray[500] }}>Pacientes na coorte</div>
          <div style={{ fontSize: "30px", fontWeight: 800, color: theme.primary[700] }}>
            {coorte.length}
          </div>
        </div>
        <div style={{ flex: 1 }}>
          <label style={labelMini}>Estratificar por</label>
          <select
            value={estratificarPor}
            onChange={(e) => setEstratificarPor(e.target.value)}
            style={{ ...inputStyle, minWidth: "220px" }}
          >
            <option value="">— sem estratificação —</option>
            {camposEstratificaveis.map((c) => (
              <option key={c.chave} value={c.chave}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
        {coortePequena && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 12px",
              background: theme.status.warning.bg,
              border: `1px solid ${theme.status.warning.border}`,
              borderRadius: theme.borderRadius.md,
              color: theme.status.warning.text,
              fontSize: "12px",
              maxWidth: "320px",
            }}
          >
            <AlertTriangle size={16} />
            Coorte com menos de {N_MINIMO_CELULA} pacientes — resultados mascarados
            (anti-reidentificação).
          </div>
        )}
      </div>

      {/* B242 — Pacientes da coorte (lista clínica IDENTIFICADA, uso de gestão) */}
      {!coorteVazia && (
        <div style={card}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
            <Users size={16} color={theme.gray[600]} />
            <strong style={{ fontSize: "14px", color: theme.gray[700] }}>Pacientes da coorte</strong>
            <span style={{ fontSize: "11px", color: theme.gray[400] }}>
              visão clínica identificada (com nome) — uso de gestão; não sai no export de pesquisa
            </span>
            <button
              onClick={() => setMostrarPacientes((v) => !v)}
              style={{
                marginLeft: "auto",
                padding: "6px 12px",
                background: mostrarPacientes ? theme.primary[50] : theme.primary[600],
                color: mostrarPacientes ? theme.primary[700] : "white",
                border: mostrarPacientes ? `1px solid ${theme.primary[300]}` : "none",
                borderRadius: theme.borderRadius.md,
                fontSize: "12px",
                fontWeight: 600,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {mostrarPacientes ? "Ocultar lista" : `Ver lista (${linhasCoorte.length})`}
            </button>
          </div>
          {mostrarPacientes && (
            <div style={{ marginTop: "12px" }}>
              <TabelaPacientesPesquisa
                linhas={linhasCoorte}
                onAbrir={abrirPaciente}
                colunas={["grupo", "status", "rgct"]}
              />
            </div>
          )}
        </div>
      )}

      {/* Table 1 */}
      {coorteVazia ? (
        <div style={{ ...card, textAlign: "center", color: theme.gray[500] }}>
          Nenhum paciente atende aos filtros.
        </div>
      ) : (
        <div style={{ ...card, overflowX: "auto" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexWrap: "wrap",
              marginBottom: "4px",
            }}
          >
            <div style={{ fontSize: "14px", fontWeight: 700, color: theme.gray[700] }}>
              Tabela descritiva (Table 1)
            </div>
            <div
              style={{
                marginLeft: "auto",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                flexWrap: "wrap",
              }}
            >
              <select
                value={finalidade}
                onChange={(e) => setFinalidade(e.target.value)}
                title="Finalidade declarada do export (registrada no arquivo)"
                style={{ ...inputStyle, fontSize: "12px", padding: "6px 8px" }}
              >
                <option value="Gestão/qualidade interna">Gestão/qualidade interna</option>
                <option value="Pesquisa científica (requer CEP/TCLE)">
                  Pesquisa científica (CEP/TCLE)
                </option>
              </select>
              <button
                onClick={exportarCSV}
                title="Baixa a Table 1 (agregada, mascarada) em CSV"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "6px 12px",
                  background: "white",
                  color: theme.primary[700],
                  border: `1px solid ${theme.primary[300]}`,
                  borderRadius: theme.borderRadius.md,
                  fontSize: "12px",
                  fontWeight: 600,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                <Download size={14} /> Exportar CSV
              </button>
              <button
                onClick={interpretarComIA}
                disabled={carregandoInterpretacao}
                title="A IA lê os números já calculados e escreve um resumo descritivo"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "6px 12px",
                  background: carregandoInterpretacao ? theme.gray[300] : theme.primary[600],
                  color: "white",
                  border: "none",
                  borderRadius: theme.borderRadius.md,
                  fontSize: "12px",
                  fontWeight: 600,
                  cursor: carregandoInterpretacao ? "wait" : "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                <FileText size={14} /> {carregandoInterpretacao ? "Interpretando…" : "Interpretar com IA"}
              </button>
            </div>
          </div>
          <div style={{ fontSize: "11px", color: theme.gray[400], marginBottom: "12px" }}>
            “Faltantes” = pacientes sem o dado. Mediana [Q1–Q3] e proporções calculadas pelo código.
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
            <thead>
              <tr>
                <th style={thStyle}>Variável</th>
                <th style={{ ...thStyle, textAlign: "right" }}>Faltantes</th>
                {colunas.map((c) => (
                  <th key={c} style={{ ...thStyle, textAlign: "left" }}>
                    {c}
                    {estratificarPor && c !== "Total" ? "" : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tabela.map((linha) => (
                <LinhaVariavel key={linha.campo} linha={linha} colunas={colunas} />
              ))}
            </tbody>
          </table>

          {(carregandoInterpretacao || erroInterpretacao || interpretacao) && (
            <div
              style={{
                marginTop: "16px",
                paddingTop: "12px",
                borderTop: `1px solid ${theme.gray[200]}`,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px" }}>
                <Sparkles size={14} color={theme.primary[600]} />
                <strong style={{ fontSize: "13px", color: theme.primary[800] }}>
                  Interpretação da IA (descritiva, exploratória)
                </strong>
              </div>
              {carregandoInterpretacao && (
                <p style={{ fontSize: "13px", color: theme.gray[500] }}>
                  🔬 Lendo os números e redigindo o resumo…
                </p>
              )}
              {erroInterpretacao && !carregandoInterpretacao && (
                <p style={{ fontSize: "13px", color: theme.status.error.text }}>{erroInterpretacao}</p>
              )}
              {interpretacao && !carregandoInterpretacao && (
                <>
                  <div
                    style={{
                      fontSize: "11px",
                      color: theme.gray[500],
                      background: theme.gray[50],
                      border: `1px solid ${theme.gray[200]}`,
                      borderRadius: theme.borderRadius.md,
                      padding: "8px 10px",
                      marginBottom: "10px",
                    }}
                  >
                    Gerada por IA a partir dos números acima — <strong>não recalcula</strong> e não
                    substitui análise estatística formal. Leitura descritiva/exploratória; revise antes
                    de usar.
                  </div>
                  <TextoAnaliseMarkdown texto={interpretacao} />
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* B242 MVP2 — Visualização (gráfico da distribuição na coorte) */}
      {!coorteVazia && (
        <div style={card}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              flexWrap: "wrap",
              marginBottom: "10px",
            }}
          >
            <BarChart3 size={16} color={theme.gray[600]} />
            <strong style={{ fontSize: "14px", color: theme.gray[700] }}>Visualização</strong>
            <select
              value={campoGrafico}
              onChange={(e) => setCampoGrafico(e.target.value)}
              style={{ ...inputStyle, marginLeft: "auto", minWidth: "220px" }}
            >
              <option value="">— escolha uma variável —</option>
              {Array.from(
                new Set(
                  CAMPOS_PESQUISA.filter((c) => c.tipo !== "texto").map((c) => c.grupo || "Outros")
                )
              ).map((grupo) => (
                <optgroup key={grupo} label={grupo}>
                  {CAMPOS_PESQUISA.filter(
                    (c) => c.tipo !== "texto" && (c.grupo || "Outros") === grupo
                  ).map((c) => (
                    <option key={c.chave} value={c.chave}>
                      {c.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
          {campoGrafico && CAMPO_POR_CHAVE[campoGrafico] ? (
            <GraficoCoorte
              registros={coorte}
              campo={CAMPO_POR_CHAVE[campoGrafico]}
              estratificarPor={estratificarPor || undefined}
            />
          ) : (
            <p style={{ fontSize: "13px", color: theme.gray[500], margin: 0 }}>
              Escolha uma variável para ver a distribuição na coorte — numérica vira{" "}
              <strong>box plot</strong>, categórica vira <strong>barras</strong>. Se você estratificar
              (acima), o gráfico compara os subgrupos. Subgrupos com menos de {N_MINIMO_CELULA}{" "}
              pacientes são mascarados.
            </p>
          )}
        </div>
      )}
      </>
      )}
    </div>
  );
}

// Painel de ajuda (B242/AI_111): explica filtro × estratificação e como ler a tabela.
function PainelInstrucoes() {
  const h: React.CSSProperties = {
    fontSize: "14px",
    fontWeight: 700,
    color: theme.gray[800],
    margin: "14px 0 4px",
  };
  const p: React.CSSProperties = {
    fontSize: "13px",
    color: theme.gray[700],
    lineHeight: 1.6,
    margin: "0 0 6px",
  };
  const li: React.CSSProperties = { ...p, margin: "2px 0" };
  const destaque: React.CSSProperties = { color: theme.primary[700], fontWeight: 600 };

  return (
    <div
      style={{
        ...card,
        background: theme.primary[50],
        border: `1px solid ${theme.primary[200]}`,
      }}
    >
      <div style={{ fontSize: "15px", fontWeight: 700, color: theme.primary[800], marginBottom: "4px" }}>
        Como usar a Pesquisa Clínica
      </div>
      <p style={p}>
        Esta tela responde: <strong>“quantos dos meus pacientes são X?”</strong> e{" "}
        <strong>“como esse grupo se parece?”</strong>. Quem conta é o código (a IA não conta).
        Os dados são pseudonimizados (sem nome/CPF) e células com menos de {N_MINIMO_CELULA}{" "}
        pacientes são mascaradas (&lt; {N_MINIMO_CELULA}).
      </p>

      <div style={h}>1. Construir coorte (o filtro)</div>
      <p style={li}>• <strong>Campo</strong>: qual característica (Idade, Grupo, FAN, MELD…).</p>
      <p style={li}>• <strong>Condição</strong>: como comparar (é igual a, maior que, entre, está em branco…).</p>
      <p style={li}>• <strong>Valor</strong>: o quê (ex.: Fígado, 18, 1/320…).</p>
      <p style={li}>• <strong>+ Adicionar filtro</strong>: aplica a regra (vira uma etiqueta). O <strong>X</strong> remove; <strong>limpar tudo</strong> zera.</p>

      <div style={h}>2. E (todos) / OU (qualquer)</div>
      <p style={p}>
        Com vários filtros: <strong>E</strong> exige cumprir <em>todos</em>; <strong>OU</strong>{" "}
        basta cumprir <em>um</em>.
      </p>

      <div style={h}>3. Pacientes na coorte</div>
      <p style={p}>É o número (n) que sobrou depois dos filtros — o tamanho do seu grupo de estudo.</p>

      <div style={h}>4. Estratificar por — a diferença</div>
      <p style={p}>
        <span style={destaque}>Filtro = quem ENTRA</span> (reduz a coorte).{" "}
        <span style={destaque}>Estratificar = como DIVIDIR</span> a coorte em colunas lado a lado
        para comparar subgrupos — <strong>não tira ninguém</strong>. Ex.: filtrar “Grupo = Fígado”
        e estratificar por “Sexo” compara homens × mulheres só entre os de Fígado.
      </p>

      <div style={h}>5. Como ler a tabela (Table 1)</div>
      <p style={li}>• <strong>Variável numérica</strong> (idade, MELD…): <strong>Mediana [Q1–Q3]</strong> (valor do meio e metade central), <strong>Média (DP)</strong> e <strong>Mín–Máx</strong>.</p>
      <p style={li}>• <strong>Variável categórica</strong> (sexo, sorologia…): <strong>contagem (%)</strong> por categoria; a % é sobre quem <em>tem</em> o dado.</p>
      <p style={li}>• <strong>Faltantes</strong>: pacientes sem aquele dado (qualidade do registro).</p>
      <p style={li}>• <strong>&lt; {N_MINIMO_CELULA}</strong>: contagens pequenas são mascaradas (privacidade).</p>

      <div style={h}>6. Cargas virais</div>
      <p style={p}>
        Cada carga viral tem <strong>“(carga)”</strong> = o número (só dos detectáveis, p/ a mediana
        não usar 0) e <strong>“(status)”</strong> = Detectável / Não detectado / Detectável (não
        quantificável). Para ver % de cada situação, estratifique pelo “(status)”.
      </p>

      <div style={h}>Dicas</div>
      <p style={li}>• <strong>Comparar grupos:</strong> sem filtro, estratifique por “Grupo (linha de cuidado)”.</p>
      <p style={li}>• <strong>Pré-Tx de fígado:</strong> filtre “Grupo = Fígado” <em>E</em> “Status de transplante = Pré-transplante”, e estratifique por “Sexo”.</p>
    </div>
  );
}

// B242 MVP3a — Painel da PROPOSTA da IA: resumo + etiquetas (já saneadas pelo
// validador) + avisos do que a IA não mapeou / o que o código descartou.
// Nada conta até o médico clicar "Aplicar".
function PropostaIA({
  proposta,
  rotuloFiltro,
  onAplicar,
  onAdicionar,
  onDescartar,
}: {
  proposta: PlanoIAValidado;
  rotuloFiltro: (f: Filtro) => string;
  onAplicar: () => void;
  onAdicionar: (f: Filtro) => void;
  onDescartar: () => void;
}) {
  const semNada = proposta.filtros.length === 0 && !proposta.estratificarPor;
  const estrato = proposta.estratificarPor
    ? CAMPO_POR_CHAVE[proposta.estratificarPor]?.label
    : "";

  return (
    <div
      style={{
        marginTop: "12px",
        padding: "12px",
        background: "white",
        border: `1px solid ${theme.primary[200]}`,
        borderRadius: theme.borderRadius.md,
      }}
    >
      <div style={{ fontSize: "12px", fontWeight: 700, color: theme.gray[700], marginBottom: "6px" }}>
        Proposta da IA — revise antes de aplicar
      </div>

      {proposta.resumo && (
        <p style={{ fontSize: "13px", color: theme.gray[800], margin: "0 0 8px", fontStyle: "italic" }}>
          “{proposta.resumo}”
        </p>
      )}

      {proposta.filtros.length > 0 ? (
        <>
          <div style={{ fontSize: "11px", color: theme.gray[500], marginBottom: "4px" }}>
            Filtros ({proposta.conector === "OU" ? "qualquer um" : "todos juntos"}):
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "8px" }}>
            {proposta.filtros.map((f, i) => (
              <span
                key={i}
                style={{
                  padding: "4px 10px",
                  background: theme.primary[50],
                  border: `1px solid ${theme.primary[200]}`,
                  borderRadius: theme.borderRadius.full,
                  fontSize: "12px",
                  color: theme.primary[800],
                }}
              >
                {rotuloFiltro(f)}
              </span>
            ))}
          </div>
        </>
      ) : (
        <p style={{ fontSize: "12px", color: theme.gray[500], margin: "0 0 8px" }}>
          Nenhum filtro — a coorte seria toda a base.
        </p>
      )}

      {estrato && (
        <div style={{ fontSize: "12px", color: theme.gray[600], marginBottom: "8px" }}>
          Estratificar por: <strong>{estrato}</strong>
        </div>
      )}

      {proposta.naoMapeado.length > 0 && (
        <div
          style={{
            fontSize: "12px",
            color: theme.status.warning.text,
            background: theme.status.warning.bg,
            border: `1px solid ${theme.status.warning.border}`,
            borderRadius: theme.borderRadius.md,
            padding: "8px 10px",
            marginBottom: "8px",
          }}
        >
          <AlertTriangle size={13} style={{ marginRight: "4px", verticalAlign: "middle" }} />
          A IA não encontrou campo para: {proposta.naoMapeado.join("; ")}. Esses critérios não entraram
          (ainda não há campo estruturado para eles).
        </div>
      )}

      {proposta.aproximacoes.length > 0 && (
        <div style={{ marginBottom: "8px" }}>
          <div style={{ fontSize: "11px", color: theme.gray[500], marginBottom: "6px" }}>
            Aproximações sugeridas (campos estruturados mais próximos) — clique para adicionar:
          </div>
          {proposta.aproximacoes.map((a, i) => (
            <div
              key={i}
              style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px", flexWrap: "wrap" }}
            >
              <button
                onClick={() => onAdicionar(a.filtro)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "5px",
                  padding: "4px 10px",
                  background: "white",
                  border: `1px dashed ${theme.primary[400]}`,
                  borderRadius: theme.borderRadius.full,
                  fontSize: "12px",
                  fontWeight: 600,
                  color: theme.primary[700],
                  cursor: "pointer",
                }}
              >
                <Plus size={12} /> {rotuloFiltro(a.filtro)}
              </button>
              {a.motivo && (
                <span style={{ fontSize: "11px", color: theme.gray[500] }}>{a.motivo}</span>
              )}
            </div>
          ))}
        </div>
      )}

      {proposta.descartados.length > 0 && (
        <div style={{ fontSize: "11px", color: theme.gray[500], marginBottom: "8px" }}>
          Descartado pelo sistema: {proposta.descartados.join("; ")}.
        </div>
      )}

      <div style={{ display: "flex", gap: "8px" }}>
        <button
          onClick={onAplicar}
          disabled={semNada}
          style={{
            padding: "8px 16px",
            background: semNada ? theme.gray[300] : theme.gradients.primary,
            color: "white",
            border: "none",
            borderRadius: theme.borderRadius.md,
            fontSize: "13px",
            fontWeight: 600,
            cursor: semNada ? "not-allowed" : "pointer",
          }}
        >
          Aplicar coorte
        </button>
        <button
          onClick={onDescartar}
          style={{
            padding: "8px 16px",
            background: "white",
            color: theme.gray[600],
            border: `1px solid ${theme.gray[300]}`,
            borderRadius: theme.borderRadius.md,
            fontSize: "13px",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Descartar
        </button>
      </div>
    </div>
  );
}

const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "8px 10px",
  borderBottom: `2px solid ${theme.gray[200]}`,
  fontSize: "12px",
  fontWeight: 700,
  color: theme.gray[600],
  whiteSpace: "nowrap",
};
const tdStyle: React.CSSProperties = {
  padding: "8px 10px",
  borderBottom: `1px solid ${theme.gray[100]}`,
  color: theme.gray[800],
  verticalAlign: "top",
};

// Máscara n<5: esconde contagens pequenas (e resumos numéricos com poucos n).
function textoCelula(c: CelulaTabela1): string {
  const pequeno = c.pequeno || (typeof c.n === "number" && c.n > 0 && c.n < N_MINIMO_CELULA);
  return pequeno ? `< ${N_MINIMO_CELULA}` : c.texto;
}

function LinhaVariavel({ linha, colunas }: { linha: LinhaTabela1; colunas: string[] }) {
  // Rótulos canônicos = ordem da coluna "Total" (categóricos podem variar entre
  // colunas; usamos a "Total" como referência e completamos o que faltar).
  const porColuna: Record<string, CelulaTabela1[]> = {};
  for (const cc of linha.celulasPorColuna) porColuna[cc.coluna] = cc.celulas;
  const totalCells = porColuna["Total"] || [];
  const rotulos = totalCells.map((c) => c.rotulo);

  return (
    <>
      {rotulos.map((rotulo, idx) => (
        <tr key={`${linha.campo}-${idx}`}>
          {idx === 0 && (
            <td style={{ ...tdStyle, fontWeight: 600 }} rowSpan={rotulos.length || 1}>
              {linha.label}
            </td>
          )}
          {idx === 0 && (
            <td
              style={{ ...tdStyle, textAlign: "right", color: theme.gray[500] }}
              rowSpan={rotulos.length || 1}
            >
              {linha.faltantes} ({linha.faltantesPct}%)
            </td>
          )}
          {colunas.map((col) => {
            const cel = (porColuna[col] || []).find((c) => c.rotulo === rotulo);
            return (
              <td key={col} style={tdStyle}>
                <span style={{ color: theme.gray[400], marginRight: "6px" }}>{rotulo}:</span>
                {cel ? textoCelula(cel) : "—"}
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}
